import { memo, useState, useEffect } from 'react';
import SnowflakesAnimation from './SnowflakesAnimation';
import FireworksAnimation from './FireworksAnimation';
import EasterAnimation from './EasterAnimation';
import { isChristmasSeason, isNewYearSeason, isEasterSeason, isSpecialEffectsEnabled } from './index';

/**
 * Wrapper component that conditionally renders special decorations
 * Shows snowflakes during Christmas season (Dec 15-26)
 * Shows fireworks during New Year season (Dec 27 - Jan 3)
 * Shows hopping rabbits during Easter season (Good Friday - Easter Monday)
 * Only renders if special effects are enabled in user settings
 * Delayed by 3 seconds after page load
 */
function SpecialWrapper({ children }) {
  const [effectsEnabled, setEffectsEnabled] = useState(isSpecialEffectsEnabled());
  const [showEffects, setShowEffects] = useState(false);
  const [showChristmas, setShowChristmas] = useState(isChristmasSeason());
  const [showNewYear, setShowNewYear] = useState(isNewYearSeason());
  const [showEaster, setShowEaster] = useState(isEasterSeason());

  // Delay showing effects by 3 seconds after page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowEffects(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Re-check season status every minute so decorations appear/disappear
  // without requiring a page reload when the season boundary is crossed
  useEffect(() => {
    const interval = setInterval(() => {
      setShowChristmas(isChristmasSeason());
      setShowNewYear(isNewYearSeason());
      setShowEaster(isEasterSeason());
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  // Listen for localStorage changes (when user toggles setting)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'abs_special_effects_enabled') {
        setEffectsEnabled(e.newValue === 'true');
      }
    };

    // Also listen for custom event for same-tab updates
    const handleCustomEvent = () => {
      setEffectsEnabled(isSpecialEffectsEnabled());
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('specialEffectsToggle', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('specialEffectsToggle', handleCustomEvent);
    };
  }, []);

  return (
    <>
      {children}
      {showEffects && effectsEnabled && showChristmas && <SnowflakesAnimation />}
      {showEffects && effectsEnabled && showNewYear && <FireworksAnimation />}
      {showEffects && effectsEnabled && showEaster && <EasterAnimation />}
    </>
  );
}

export default memo(SpecialWrapper);

